<template>
  <div class="pt-0">
    <NuxtLayout
      name="teasers"
      v-bind="{ loading }"
      :no-results="!teasers.length"
      @refresh="handleRefetch"
    >
      <component
        :is="getTeaserComponent(teaser)"
        v-for="(teaser, index) in teasers"
        :key="`lotteries_${index}_${locale}`"
        v-bind="{
          ...teaser,
          licensedTerritory,
        }"
        :background-image="isFreeToPlayTeaser(teaser) ? freeToPlayWorldCupBackground : undefined"
        :logo="isFreeToPlayTeaser(teaser) ? freeToPlayWorldCupLogo : undefined"
        @click="handleTeaserClick(teaser)"
        @update-teasers="handleRefetch"
      />
    </NuxtLayout>
  </div>
</template>

<script lang="ts" setup>
import type { ProductCatalogElement } from '#api/shared/@types/generated/backend/graphql-schema-types'

import { KnownProductType } from '#api/shared/@types/generated/backend/graphql-schema-types'
import FreeToPlayTeaser from '#free-to-play/app/components/FreeToPlayTeaser.vue'
import { FREE_TO_PLAY_ITEM_NAME } from '#free-to-play/app/constants/constants'
import LotteryTeaser from '#product/app/components/LotteryTeaser.vue'
import freeToPlayWorldCupBackground from '~/assets/img/freeToPlay/background-mystery-scratch-world-cup.png'
import freeToPlayWorldCupLogo from '~/assets/img/freeToPlay/logo-mystery-scratch-world-cup.svg'

import { useFetchOptions } from '../utils/fetchOptions'

const { locale } = useI18n()
const licensedTerritory = useLicensedTerritory({ raw: true })

const {
  loading,
  refetch: lotteriesRefetch,
  result: lotteriesResult,
} = useProductCatalogQuery(
  { licensedTerritory, productType: KnownProductType.Lottery },
  useFetchOptions(),
)

const teasers = computed(() => {
  const result = lotteriesResult.value?.productCatalog
  return isGraphqlSuccess(result) ? (result.elements as ProductCatalogElement[]) : []
})

const { isFreeToPlayTeaser } = useFreeToPlay()

const handleRefetch = () => lotteriesRefetch()

const TRACKING_LIST_NAME = 'Home Lottery Teasers'
const TRACKING_LIST_ID = 'home_lottery_teasers'

const { createTeaserListItems } = useTrackingData()
const { pushSelectListItemEvent, pushViewListEvent } = useTracking()
const { logTrackingError } = useTrackingErrorLog()

const trackingTeaserListItems = ref<ReturnType<typeof createTeaserListItems>>([])

const getTeaserComponent = (teaser: ProductCatalogElement) => {
  if (teaser.__typename === 'LotteryTeaserPayload') return LotteryTeaser
  if (isFreeToPlayTeaser(teaser)) return FreeToPlayTeaser
  return null
}

const handleTeaserClick = (teaser: ProductCatalogElement) => {
  const teaserItem = trackingTeaserListItems.value.find((item) => {
    switch (teaser.__typename) {
      case 'FreeToPlayTeaserPayload':
        return item.item_name === FREE_TO_PLAY_ITEM_NAME
      case 'LotteryTeaserPayload':
        return item.item_id === teaser.id
      default:
        return false
    }
  })

  if (!teaserItem) return

  try {
    pushSelectListItemEvent({
      items: [teaserItem],
      list_id: TRACKING_LIST_ID,
      list_name: TRACKING_LIST_NAME,
    })
  } catch (err) {
    logTrackingError('select_list_item', err)
  }
}

let unwatchTeasers: Maybe<() => void> = null
unwatchTeasers = watchImmediate(teasers, (list) => {
  if (!list || list.length === 0) return

  try {
    trackingTeaserListItems.value = createTeaserListItems(list)
    pushViewListEvent({
      items: trackingTeaserListItems.value,
      list_id: TRACKING_LIST_ID,
      list_name: TRACKING_LIST_NAME,
    })

    if (unwatchTeasers) unwatchTeasers()
  } catch (err) {
    logTrackingError('view_list', err)
  }
})
</script>
